import { queryOptions, keepPreviousData } from '@tanstack/react-query'
import queryString from 'query-string'

import { apiClient, SEARCH_API_URL } from '.'
import type {
  ImportIngredientPayload,
  Ingredient,
  IngredientGroup,
  IngredientGroupListItem,
  IngredientGroupUpdate,
  IngredientUnit,
  NutsRegion,
  SearchIngredient,
  TempIngredientUnit,
  WeightYieldFactor,
} from './types'
import { IngredientsSearchParams } from '@/routes/ingredients.fcdb'

const DEFAULT_PAGE_SIZE = 20

type IngredientsFetchParams = {
  externalID?: string[]
  nutrientInformation?: 'MISSING_MACRO' | 'MISSING_MICRO'
  includeHidden?: boolean
  idGroup?: number
  languaLID?: string
  includeLanguaLSubtree?: boolean // True
  expand?: boolean // False
  country?: string
  onlyUsedInRecipes?: boolean
}

export async function fetchIngredients(params?: IngredientsFetchParams) {
  return apiClient
    .get<SearchIngredient[]>('/ingredient', {
      params,
      paramsSerializer: (params) => {
        return queryString.stringify(params)
      },
    })
    .then((res) => res.data)
}

export const ingredientsQuery = (query: IngredientsFetchParams = {}) =>
  queryOptions({
    queryKey: ['ingredients', query],
    queryFn: () => fetchIngredients(query),
  })

export async function fetchIngredient(ingId: string): Promise<Ingredient> {
  return apiClient.get(`/ingredient/${ingId}`).then((res) => res.data)
}

export const ingredientQuery = (ingId: string) => {
  return queryOptions({
    queryKey: ['ingredient', ingId],
    queryFn: () => fetchIngredient(ingId),
  })
}

export async function saveIngredient(ingredient: Ingredient) {
  return apiClient.post(`/ingredient/${ingredient.idIngredient}`, ingredient)
}

export async function duplicateIngredient(idIngredient: number) {
  return apiClient.post(`/ingredient/${idIngredient}/duplicate`)
}

export async function deleteIngredient(idIngredient: number) {
  return apiClient.delete(`/ingredient/${idIngredient}`)
}

export async function importIngredient(ingredient: ImportIngredientPayload) {
  return apiClient.post(`/ingredient/import`, ingredient)
}

export async function toggleIngredientVisibility(idIngredient: number, state: boolean) {
  return apiClient.post(`/ingredient/${idIngredient}/visibility`, null, {
    params: {
      hidden: state,
    },
  })
}

async function fetchWeightYieldFactors(): Promise<WeightYieldFactor[]> {
  return apiClient.get('/weightYieldFactors/').then((res) => res.data)
}

export const weightYieldFactorsQuery = queryOptions({
  queryKey: ['weightYieldFactors'],
  queryFn: fetchWeightYieldFactors,
})

async function fetchNutrientRetentionFactors() {
  return apiClient.get('/nutrientRetentionFactors/').then((res) => res.data)
}

export const nutrientRetentionFactorsQuery = queryOptions({
  queryKey: ['nutrientRetentionFactors'],
  queryFn: fetchNutrientRetentionFactors,
})

export async function fetchNuts() {
  return apiClient.get<[NutsRegion]>('/nuts').then((res) => res.data)
}

// Ingredient group
export async function fetchIngredientGroups() {
  return apiClient.get<IngredientGroupListItem[]>('/ingredientgroup').then((res) => res.data)
}

export async function fetchIngredientGroup(id: number) {
  return apiClient.get<IngredientGroup>(`/ingredientgroup/${id}`).then((res) => res.data)
}

export async function createIngredientGroup(group: IngredientGroupUpdate) {
  return apiClient.post<IngredientGroup>('/ingredientgroup', group).then((res) => res.data)
}

export async function updateIngredientGroup(id: number, group: IngredientGroupUpdate) {
  return apiClient.put(`/ingredientgroup/${id}`, group).then((res) => res.data)
}

export async function deleteIngredientGroup(id: number) {
  return apiClient.delete(`/ingredientgroup/${id}`)
}

export const ingredientGroupsQuery = queryOptions({
  queryKey: ['ingredientgroups'],
  queryFn: fetchIngredientGroups,
})

export const ingredientGroupQuery = (id: number) => {
  return queryOptions({
    queryKey: ['ingredientgroup', id],
    queryFn: () => fetchIngredientGroup(id),
  })
}

export async function fetchAvailableUnits() {
  return apiClient.get<IngredientUnit[]>('/ingredient/available-units').then((res) => res.data)
}

export async function fetchIngredientUnits(idIngredient: number) {
  return apiClient
    .get<IngredientUnit[]>(`/ingredient/${idIngredient}/units`)
    .then((res) => res.data)
}

export async function saveIngredientUnits(
  idIngredient: number,
  units: (IngredientUnit | TempIngredientUnit)[],
) {
  return apiClient
    .post<(IngredientUnit | TempIngredientUnit)[]>(`/ingredient/${idIngredient}/units`, units)
    .then((res) => res.data)
}


export async function searchIngredients(query: IngredientsSearchParams) {
  const page = query.page ? query.page : 0
  const pageSize = query.pageSize ? query.pageSize : DEFAULT_PAGE_SIZE

  return apiClient.get<Ingredient[]>(`/ingredients`, {
    params: {
      ...query,
      offset: pageSize * page,
      name: query.search,
      idGroup: query.foodGroup,
      count: pageSize,
      model: 'NUTRITIONIST_UI'
    },
    baseURL: SEARCH_API_URL,
    paramsSerializer: (params) => {
      return queryString.stringify(params)
    },
  })
}

export const ingredientsSearchQuery = (query: IngredientsSearchParams) => {
  return queryOptions({
    queryKey: ['ingredients-search', query],
    queryFn: () => searchIngredients(query),
    placeholderData: keepPreviousData,
  })
}